import React from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';

const SITE_URL = 'https://www.virgil.com.ai';

const SEO = ({
  title = 'Virgil | A Storytelling Workspace with Multiple AI Models',
  description = 'Outline, draft, revise, format, and explore visual ideas in one writing workspace built for screenplays, novels, podcasts, and pitches.',
  keywords = 'AI creative writing software, screenwriting AI, novel writing AI, AI writer, multi-model writing workspace, screenplay formatting',
  image = '/social/virgil-og-logo-compact-v2.jpg',
  imageAlt = 'Virgil logo on a dark background.',
  imageWidth = '1200',
  imageHeight = '500',
  url,
  type = 'website',
  noindex = false,
  includeStructuredData,
}) => {
  const location = useLocation();
  const pathname = location.pathname.replace(/\/+$/, '') || '/';
  const pageUrl = url || `${SITE_URL}${pathname === '/' ? '' : pathname}`;
  const imageUrl = image.startsWith('http://') || image.startsWith('https://')
    ? image
    : `${SITE_URL}${image.startsWith('/') ? image : `/${image}`}`;
  const shouldIncludeStructuredData =
    includeStructuredData ?? pathname === '/';

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    '@id': `${SITE_URL}/#software`,
    name: 'Virgil',
    alternateName: [
      'Virgil AI Writer',
      'Virgil Screenwriting AI',
      'Virgil Creative Writing Workspace',
    ],
    applicationCategory: 'BusinessApplication',
    applicationSubCategory: 'Creative Writing Software',
    operatingSystem: 'macOS (Apple silicon), Windows',
    description,
    url: SITE_URL,
    logo: `${SITE_URL}/icons/png/virgil-profile-mark.webp`,
    offers: {
      '@type': 'AggregateOffer',
      lowPrice: '5',
      highPrice: '50',
      priceCurrency: 'USD',
      offerCount: '3',
    },
    creator: {
      '@type': 'Organization',
      '@id': `${SITE_URL}/#organization`,
      name: 'Virgil AI Inc',
      url: SITE_URL,
    },
    featureList: [
      'Multi-model AI writing workspace',
      'Screenplay and long-form writing tools',
      'Professional screenplay formatting',
      'Script coverage and revision tools',
      'Image-generation tools',
      'Video-generation tools',
    ],
  };

  return (
    <Helmet>
      <title>{title}</title>
      <meta name="title" content={title} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta
        name="robots"
        content={noindex ? 'noindex, nofollow' : 'index, follow'}
      />
      <meta name="language" content="English" />
      <meta name="author" content="Virgil AI" />
      <link rel="canonical" href={pageUrl} />

      <meta property="og:type" content={type} />
      <meta property="og:url" content={pageUrl} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={imageUrl} />
      <meta property="og:image:secure_url" content={imageUrl} />
      <meta property="og:image:width" content={imageWidth} />
      <meta property="og:image:height" content={imageHeight} />
      <meta property="og:image:alt" content={imageAlt} />
      <meta property="og:site_name" content="Virgil" />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={pageUrl} />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={imageUrl} />
      <meta name="twitter:image:alt" content={imageAlt} />

      {shouldIncludeStructuredData && (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      )}
      <meta
        name="viewport"
        content="width=device-width, initial-scale=1, maximum-scale=5"
      />
    </Helmet>
  );
};

export default SEO;
